import { FetchRequest } from '@rails/request.js';
import Sortable from 'sortablejs';
import AxisLockedSortableController from './axis_locked_sortable_base';

const COLLAPSED_EXPAND_DELAY = 400;
const COMPANION_CLASS = 'billable-item--drag-companion';
const DRAG_IMAGE_CLASS = 'billable-drag-image';
const DRAG_IMAGE_SOURCES = [
  'img.image:not(.preview)',
  'img.billable-item__image-v2:not(.preview)',
  '.billable-item__placeholder-image',
  '.data-table__swatch',
].join(', ');

let dragOrigins = [];
let dragCompanions = [];

export default class extends AxisLockedSortableController {
  static values = { moveUrl: String };

  #expandTimer;
  #expandTarget;

  _extraOptions() {
    return {
      onMove: (event) => this.#allowMove(event),
    };
  }

  _groupOptions() {
    return {
      group: {
        name: 'billable-items',
        pull: true,
        put: (to, from, dragEl) => this.#canReceive(to.el, from.el, dragEl),
      },
    };
  }

  _updateSortOrder(event) {
    this.dispatch('changed');
  }

  _onStartDragging(event) {
    super._onStartDragging(event);
    dragOrigins = this.#originsFor(event.item);
    this.#fadeCompanions(event.item, dragOrigins);
    this.#showDragImage(event, dragOrigins.length);
  }

  #showDragImage(event, count) {
    const ghost = Sortable.ghost;

    if (!ghost) {
      return;
    }

    const row = event.item;
    const multiple = count > 1;
    const handle = document.createElement('i');
    handle.className = `fa-sharp fa-solid fa-grip-dots-vertical ${DRAG_IMAGE_CLASS}__handle`;
    handle.setAttribute('aria-hidden', 'true');

    ghost.className = `sortable-fallback ${DRAG_IMAGE_CLASS}`;
    ghost.classList.toggle(`${DRAG_IMAGE_CLASS}--multiple`, multiple);
    ghost.replaceChildren(
      handle,
      ...(multiple ? [this.#dragCountFor(count)] : [this.#dragImageFor(row), this.#titleFor(row)])
    );
    ghost.style.width = '';
    ghost.style.height = '';
    ghost.style.opacity = '';
    this.#alignDragImage(ghost, handle, row, event);
  }

  #dragImageFor(row) {
    const source = row.querySelector(
      `:scope > .billable-item__td--name :is(${DRAG_IMAGE_SOURCES})`
    );
    const image = source?.cloneNode(source.tagName !== 'IMG') ?? this.#dragImagePlaceholder();

    [...image.attributes].forEach(({ name }) => {
      if (name === 'id' || name.startsWith('data-')) {
        image.removeAttribute(name);
      }
    });
    image.className = `${DRAG_IMAGE_CLASS}__image`;

    return image;
  }

  #dragImagePlaceholder() {
    const placeholder = document.createElement('div');
    const icon = document.createElement('i');
    icon.className = 'fa-light fa-image';
    icon.setAttribute('aria-hidden', 'true');
    placeholder.append(icon);

    return placeholder;
  }

  #titleFor(row) {
    const column = row.querySelector(
      ':scope > .billable-item__td--name .billable-item__name-column'
    );
    const field = column?.querySelector('textarea, input');
    const title = document.createElement('span');
    title.className = `${DRAG_IMAGE_CLASS}__title`;
    title.textContent = (field ? field.value : (column?.textContent ?? '')).trim();

    return title;
  }

  #dragCountFor(count) {
    const label = document.createElement('span');
    label.className = `${DRAG_IMAGE_CLASS}__count`;
    label.textContent = `${count} Items`;

    return label;
  }

  #alignDragImage(ghost, handle, row, event) {
    const point = this.#dropPoint(event);
    const rect = row.getBoundingClientRect();
    const handleX = handle.offsetLeft + handle.offsetWidth / 2;
    const handleY = handle.offsetTop + handle.offsetHeight / 2;

    ghost.style.left = `${parseFloat(ghost.style.left) + point.clientX - rect.left - handleX}px`;
    ghost.style.top = `${parseFloat(ghost.style.top) + point.clientY - rect.top - handleY}px`;
  }

  #fadeCompanions(dragged, origins) {
    dragCompanions = this.#companionRows(dragged, origins);
    dragCompanions.forEach((row) => row.classList.add(COMPANION_CLASS));
  }

  #unfadeCompanions() {
    dragCompanions.forEach((row) => row.classList.remove(COMPANION_CLASS));
    dragCompanions = [];
  }

  _onEndDragging(event) {
    super._onEndDragging(event);
    this.#unfadeCompanions();
    this.#cancelExpand();
    this.#ungroupOnInvalidDrop(event);
    this.#moveToTopOnHeaderDrop(event);
    this.#gatherCompanionsOnReorder(event);
  }

  #gatherCompanionsOnReorder(event) {
    const origins = dragOrigins;
    dragOrigins = [];

    const reorderedInPlace = event.newIndex === event.oldIndex && !this.#droppedAboveList(event);

    if (
      origins.length === 0 ||
      event.to !== event.from ||
      this.#isSubitems(event.to) ||
      reorderedInPlace
    ) {
      return;
    }

    this.#placeCompanions(event.item, origins);
    this.#restoreSectionChrome();
    this._updateSortOrder(event);

    if (this.#anyFromOtherList(origins)) {
      return this.#persistSectionMove(event, origins);
    }
  }

  #moveToTopOnHeaderDrop(event) {
    const droppedInOwnTopLevelList = event.to === event.from && !this.#isSubitems(event.to);

    if (droppedInOwnTopLevelList && this.#droppedAboveList(event)) {
      event.to.prepend(event.item);
      this._updateSortOrder(event);
    }
  }

  #ungroupOnInvalidDrop(event) {
    const droppedInOwnGroup = event.to === event.from && this.#isSubitems(event.to);

    if (droppedInOwnGroup) {
      this.#ungroupWhenDroppedOutsideRows(event);
    }
  }

  #ungroupWhenDroppedOutsideRows(event) {
    const row = event.item;
    const subitems = event.to;
    const primary = subitems.closest('.billable-item');
    const dropPoint = this.#dropPoint(event);
    const droppedAbovePrimary = dropPoint.clientY < subitems.getBoundingClientRect().top;
    const droppedBelowGroup = dropPoint.clientY > primary.getBoundingClientRect().bottom;

    if (droppedAbovePrimary) {
      primary.before(row);
    }

    if (droppedBelowGroup) {
      primary.after(row);
    }

    if (droppedAbovePrimary || droppedBelowGroup) {
      this.#makeStandalone(row);
      this.#resetEmptyGroup(subitems);
      this._updateSortOrder(event);
    }
  }

  _onItemAdded(event) {
    const changedSection = this.#topLevelListFor(event.from) !== this.#list;
    const droppedIntoGroup = this.#isSubitems(event.to);
    const draggedOutOfGroup = this.#isSubitems(event.from);

    if (this.#droppedAboveList(event)) {
      this.#list.prepend(event.item);
      this.#makeStandalone(event.item);
    } else if (droppedIntoGroup) {
      this.#makeSecondary(event.item, event.to.closest('.billable-item'));
    } else {
      this.#makeStandalone(event.item);
    }

    if (draggedOutOfGroup) {
      this.#resetEmptyGroup(event.from);
    }

    const origins = dragOrigins;
    dragOrigins = [];
    this.#placeCompanions(event.item, origins);

    this.#restoreSectionChrome();
    this._updateSortOrder(event);

    if (changedSection || this.#anyFromOtherList(origins)) {
      return this.#persistSectionMove(event, origins);
    }
  }

  async #persistSectionMove(event, origins = []) {
    const rows = this.#inDocumentOrder([event.item, ...this.#companionRows(event.item, origins)]);
    const sectionId = this.#sectionIdFor(this.#list);

    const body = new FormData();
    rows.flatMap((row) => this.#rowIds(row)).forEach((id) => body.append('item_ids[]', id));
    if (sectionId) {
      body.append('section_id', sectionId);
    }
    body.append('position', this.#positionOf(rows[0]));
    body.append('move_method', 'drag_and_drop');

    try {
      const response = await new FetchRequest('post', this.#moveUrl, { body }).perform();

      if (response.ok) {
        this.dispatch('items-moved', { prefix: 'billable-section' });
      } else {
        this.#revertSectionMove(event, origins);
      }
    } catch (error) {
      console.error(error);
      this.#revertSectionMove(event, origins);
    }
  }

  #revertSectionMove(event, origins = []) {
    const { item: row, from, to, oldIndex } = event;

    if (origins.length > 0) {
      this.#restoreOrigins(origins);
    } else {
      from.insertBefore(row, from.children[oldIndex] ?? null);

      if (this.#isSubitems(from)) {
        this.#makeSecondary(row, from.closest('.billable-item'));
      } else {
        this.#makeStandalone(row);
      }

      if (this.#isSubitems(to)) {
        this.#resetEmptyGroup(to);
      }
    }

    this._updateSortOrder(event);
    window.alert('The item could not be moved. Please try again.');
  }

  #originsFor(dragged) {
    if (!this.#selectionCheckbox(dragged)?.checked) {
      return [];
    }

    const selected = [...this.#grid.querySelectorAll('.checkbox--group-billable-item:checked')]
      .map((checkbox) => checkbox.closest('.billable-item'))
      .filter((row) => row !== Sortable.ghost);
    const selectedSet = new Set(selected);
    const companions = selected.filter(
      (row) => row !== dragged && !dragged.contains(row) && !selectedSet.has(this.#primaryOf(row))
    );

    if (companions.length === 0) {
      return [];
    }

    return this.#inDocumentOrder([dragged, ...companions]).map((row) => ({
      row,
      parent: row.parentElement,
      next: row.nextElementSibling,
    }));
  }

  #companionRows(dragged, origins) {
    return origins.map(({ row }) => row).filter((row) => row !== dragged);
  }

  #placeCompanions(dragged, origins) {
    const rows = this.#companionRows(dragged, origins);
    const before = rows.filter((row) => this.#precedes(row, dragged));
    const after = rows.filter((row) => !before.includes(row));

    dragged.before(...before);
    dragged.after(...after);

    origins.forEach(({ row, parent }) => {
      if (row === dragged) {
        return;
      }

      this.#makeStandalone(row);

      if (this.#isSubitems(parent)) {
        this.#resetEmptyGroup(parent);
      }
    });
  }

  #restoreOrigins(origins) {
    [...origins].reverse().forEach(({ row, parent, next }) => {
      parent.insertBefore(row, next);

      if (this.#isSubitems(parent)) {
        this.#assignGroup(row, parent.closest('.billable-item').dataset.id);
      } else {
        this.#makeStandalone(row);
      }
    });
  }

  #anyFromOtherList(origins) {
    return origins.some(({ parent }) => this.#topLevelListFor(parent) !== this.#list);
  }

  #inDocumentOrder(rows) {
    return [...rows].sort((a, b) => (this.#precedes(a, b) ? -1 : 1));
  }

  #precedes(a, b) {
    return Boolean(a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING);
  }

  #primaryOf(row) {
    return row.parentElement.closest('.billable-item');
  }

  #selectionCheckbox(row) {
    return row.querySelector(':scope > .col-name .checkbox--group-billable-item');
  }

  get #grid() {
    return this.element.closest('.main-grid__body') ?? this.element;
  }

  #rowIds(row) {
    return [row, ...row.querySelectorAll('.billable-item')].map((el) => el.dataset.id);
  }

  #positionOf(row) {
    return [...this.#list.querySelectorAll('.billable-item')].indexOf(row);
  }

  #sectionIdFor(list) {
    const section = list.closest('.billable-section');

    if (!section || section.classList.contains('billable-section--unassigned')) {
      return null;
    }

    return section.dataset.sectionId;
  }

  get #moveUrl() {
    return this.application.getControllerForElementAndIdentifier(this.#list, this.identifier)
      .moveUrlValue;
  }

  #droppedAboveList(event) {
    return this.#dropPoint(event).clientY < this.#list.getBoundingClientRect().top;
  }

  #dropPoint(event) {
    return event.originalEvent.changedTouches?.[0] ?? event.originalEvent;
  }

  get #list() {
    return this.#topLevelListFor(this.element);
  }

  #topLevelListFor(element) {
    return element.closest('.billable-section, .main-grid__body');
  }

  #canReceive(toEl, dragOriginEl, dragEl) {
    if (!this.#isRow(dragEl)) {
      return false;
    }

    if (this.#isSubitems(toEl) && this.#companionRows(dragEl, dragOrigins).length > 0) {
      return false;
    }

    const targetIsSubitems = this.#isSubitems(toEl);
    const targetIsOpenGroup = targetIsSubitems && (this.#hasItems(toEl) || toEl === dragOriginEl);
    const draggedHasSubitems = Boolean(dragEl.querySelector('.subitems .billable-item'));
    const draggedIsSecondary = this.#isSubitems(dragEl.parentElement);
    const changingSection = this.#topLevelListFor(dragOriginEl) !== this.#list;

    const joiningGroup = targetIsOpenGroup && !draggedHasSubitems;
    const leavingToTopLevel = !targetIsSubitems && draggedIsSecondary;
    const movingToAnotherSection = !targetIsSubitems && changingSection;

    return joiningGroup || leavingToTopLevel || movingToAnotherSection;
  }

  #allowMove(event) {
    const collapsed = this.#collapsedSectionFor(event.to);

    if (!collapsed) {
      this.#cancelExpand();
      return true;
    }

    this.#scheduleExpand(collapsed);
    return false;
  }

  #collapsedSectionFor(list) {
    const section = list.closest('.billable-section');

    return section && !section.classList.contains('expanded') ? section : null;
  }

  #scheduleExpand(section) {
    if (this.#expandTarget === section) {
      return;
    }

    this.#cancelExpand();
    this.#expandTarget = section;
    this.#expandTimer = setTimeout(() => {
      this.dispatch('expand', { prefix: 'billable-section', target: section });
    }, COLLAPSED_EXPAND_DELAY);
  }

  #cancelExpand() {
    clearTimeout(this.#expandTimer);
    this.#expandTimer = null;
    this.#expandTarget = null;
  }

  // Sortable drops a row wherever the pointer lands, which in an empty section is past the
  // add item bar, and #droppedAboveList prepends ahead of the header. Both leave the chrome
  // out of place, and everything downstream reads the DOM order.
  #restoreSectionChrome() {
    const header = this.#list.querySelector(':scope > .billable-section__header');
    const addItemBar = this.#list.querySelector(':scope > .billable-section__add-item');

    if (header?.previousElementSibling) {
      this.#list.prepend(header);
    }

    if (addItemBar?.nextElementSibling) {
      this.#list.append(addItemBar);
    }
  }

  #isRow(element) {
    return Boolean(element?.classList?.contains('billable-item'));
  }

  #isSubitems(element) {
    return Boolean(element?.classList?.contains('subitems'));
  }

  #hasItems(subitems) {
    return Boolean(subitems.querySelector('.billable-item'));
  }

  #makeSecondary(row, primary) {
    this.#assignGroup(row, primary.dataset.id);
    this.#clearSelection(row);
  }

  #clearSelection(row) {
    const checkbox = this.#selectionCheckbox(row);

    if (!checkbox.checked) {
      return;
    }

    // Sortable re-checks inputs that were checked at drag start, after onAdd runs.
    queueMicrotask(() => {
      checkbox.checked = false;
      checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  #makeStandalone(row) {
    this.#assignGroup(row, 0);
  }

  #assignGroup(row, primaryId) {
    const primaryIdField = row.querySelector('.billable-item__primary-id');

    if (!primaryIdField) {
      return;
    }

    primaryIdField.value = primaryId;
    row.querySelector('.billable-item__group-display').value = 'primary_and_secondary';
  }

  #resetEmptyGroup(subitems) {
    const primary = subitems.closest('.billable-item');

    if (primary && !subitems.querySelector('.billable-item')) {
      this.#makeStandalone(primary);
    }
  }
}
